export interface SyncJsonStorage {
  get<T>(key: string): T | undefined;
  set<T>(key: string, item: T): void;
  remove(key: string): void;
  keys(): string[];
  clear(): void;
}

export class InMemorySyncJsonStorage implements SyncJsonStorage {
  #records: Map<string, unknown> = new Map();

  get<T>(key: string): T | undefined {
    const value = this.#records.get(key);
    return value as T | undefined;
  }

  set<T>(key: string, item: T) {
    this.#records.set(key, item);
  }

  remove(key: string) {
    this.#records.delete(key);
  }

  keys(): string[] {
    return Array.from(this.#records.keys());
  }

  clear() {
    this.#records.clear();
  }
}

export class JsonLocalStorage implements SyncJsonStorage {
  get<T>(key: string): T | undefined {
    try {
      const item = localStorage.getItem(key);
      if (item && typeof item === "string") {
        return JSON.parse(item);
      }
    } catch {
      /* ignore */
    }
  }

  set<T>(key: string, item: T) {
    try {
      const serializedItem = JSON.stringify(item);
      localStorage.setItem(key, serializedItem);
    } catch {
      /* ignore */
    }
  }

  remove(key: string) {
    localStorage.removeItem(key);
  }

  keys() {
    return Object.keys(localStorage);
  }

  clear() {
    localStorage.clear();
  }
}

export class PrefixedSyncJsonStorage implements SyncJsonStorage {
  constructor(
    private readonly prefix: string,
    private readonly syncJsonStorage: SyncJsonStorage,
  ) {}

  #getKey(key: string): string {
    return `${this.prefix}-${key}`;
  }

  get<T>(key: string): T | undefined {
    return this.syncJsonStorage.get(this.#getKey(key));
  }

  set<T>(key: string, item: T) {
    this.syncJsonStorage.set(this.#getKey(key), item);
  }

  remove(key: string) {
    this.syncJsonStorage.remove(this.#getKey(key));
  }

  keys() {
    return this.syncJsonStorage.keys();
  }

  clear() {
    const keys = this.syncJsonStorage.keys();
    const prefix = this.#getKey("");
    for (const key of keys) {
      if (key.startsWith(prefix)) {
        this.syncJsonStorage.remove(key);
      }
    }
  }
}
